export type CodeSearchQualifier =
  | 'repo'
  | 'org'
  | 'user'
  | 'enterprise'
  | 'language'
  | 'license'
  | 'path'
  | 'symbol'
  | 'content'
  | 'is'

export interface CodeSearchAtom {
  id: number
  qualifier: CodeSearchQualifier | null
  value: string
  quoted: boolean
  regex: boolean
}

export type CodeSearchExpression =
  | { type: 'atom'; atom: CodeSearchAtom }
  | { type: 'and' | 'or'; left: CodeSearchExpression; right: CodeSearchExpression }
  | { type: 'not'; expression: CodeSearchExpression }

export interface ParsedCodeSearchQuery {
  atoms: CodeSearchAtom[]
  expression: CodeSearchExpression | null
  error: string | null
}

type SearchToken =
  | { type: 'atom'; atom: CodeSearchAtom }
  | { type: 'and' | 'or' | 'not' | 'left' | 'right' }

const QUALIFIERS = new Set<CodeSearchQualifier>([
  'repo',
  'org',
  'user',
  'enterprise',
  'language',
  'license',
  'path',
  'symbol',
  'content',
  'is',
])

function readDelimited(
  input: string,
  start: number,
  delimiter: '"' | '/',
): { end: number; value: string; error: string | null } {
  let value = ''
  let index = start + 1
  while (index < input.length) {
    const character = input[index]
    if (character === delimiter) {
      return { end: index + 1, value, error: null }
    }
    if (character === '\\' && index + 1 < input.length) {
      const next = input[index + 1]
      if (delimiter === '/' || next === '"' || next === '\\') {
        value += delimiter === '/' ? `${character}${next}` : next
        index += 2
        continue
      }
    }
    value += character
    index += 1
  }
  return {
    end: input.length,
    value,
    error: delimiter === '"' ? 'Unclosed quoted search term' : 'Unclosed regular expression',
  }
}

function hasClosingRegexDelimiter(input: string, start: number): boolean {
  for (let index = start + 1; index < input.length; index += 1) {
    if (input[index] === '\\') {
      index += 1
      continue
    }
    if (input[index] !== '/') continue
    const next = input[index + 1]
    if (next === undefined || /\s|[()]/.test(next)) return true
  }
  return false
}

function tokenizeCodeSearch(input: string): { tokens: SearchToken[]; error: string | null } {
  const tokens: SearchToken[] = []
  let atomId = 0
  let index = 0

  const addAtom = (
    value: string,
    qualifier: CodeSearchQualifier | null,
    quoted: boolean,
    regex: boolean,
  ) => {
    if (!value) return
    const operator = !qualifier && !quoted && !regex ? value : ''
    if (operator === 'AND' || operator === 'OR' || operator === 'NOT') {
      tokens.push({ type: operator.toLowerCase() as 'and' | 'or' | 'not' })
      return
    }
    tokens.push({
      type: 'atom',
      atom: { id: atomId++, qualifier, value, quoted, regex },
    })
  }

  while (index < input.length) {
    const character = input[index]
    if (/\s/.test(character)) {
      index += 1
      continue
    }
    if (character === '(' || character === ')') {
      tokens.push({ type: character === '(' ? 'left' : 'right' })
      index += 1
      continue
    }
    if (character === '"' || (character === '/' && hasClosingRegexDelimiter(input, index))) {
      const result = readDelimited(input, index, character)
      if (result.error) return { tokens, error: result.error }
      addAtom(result.value, null, character === '"', character === '/')
      index = result.end
      continue
    }

    const start = index
    while (index < input.length && !/[\s():]/.test(input[index])) index += 1
    const prefix = input.slice(start, index)

    if (input[index] === ':' && QUALIFIERS.has(prefix.toLowerCase() as CodeSearchQualifier)) {
      const qualifier = prefix.toLowerCase() as CodeSearchQualifier
      index += 1
      if (index >= input.length || /\s|[()]/.test(input[index])) {
        return { tokens, error: `Missing value for ${qualifier}:` }
      }
      if (
        input[index] === '"' ||
        (input[index] === '/' && hasClosingRegexDelimiter(input, index))
      ) {
        const delimiter = input[index] as '"' | '/'
        const result = readDelimited(input, index, delimiter)
        if (result.error) return { tokens, error: result.error }
        addAtom(result.value, qualifier, delimiter === '"', delimiter === '/')
        index = result.end
      } else {
        const valueStart = index
        while (index < input.length && !/[\s()]/.test(input[index])) index += 1
        addAtom(input.slice(valueStart, index), qualifier, false, false)
      }
      continue
    }

    index = start
    while (index < input.length && !/[\s()]/.test(input[index])) index += 1
    addAtom(input.slice(start, index), null, false, false)
  }

  return { tokens, error: null }
}

function beginsExpression(token: SearchToken | undefined): boolean {
  return token?.type === 'atom' || token?.type === 'left' || token?.type === 'not'
}

export function parseCodeSearchQuery(input: string): ParsedCodeSearchQuery {
  const tokenized = tokenizeCodeSearch(input.trim())
  if (tokenized.error) return { atoms: [], expression: null, error: tokenized.error }
  const atoms = tokenized.tokens
    .filter((token): token is Extract<SearchToken, { type: 'atom' }> => token.type === 'atom')
    .map((token) => token.atom)
  if (tokenized.tokens.length === 0) return { atoms, expression: null, error: null }

  let position = 0

  const parsePrimary = (): CodeSearchExpression | null => {
    const token = tokenized.tokens[position]
    if (!token) return null
    if (token.type === 'atom') {
      position += 1
      return { type: 'atom', atom: token.atom }
    }
    if (token.type === 'left') {
      position += 1
      const expression = parseOr()
      if (tokenized.tokens[position]?.type !== 'right') return null
      position += 1
      return expression
    }
    return null
  }

  const parseNot = (): CodeSearchExpression | null => {
    if (tokenized.tokens[position]?.type === 'not') {
      position += 1
      const expression = parseNot()
      return expression ? { type: 'not', expression } : null
    }
    return parsePrimary()
  }

  const parseAnd = (): CodeSearchExpression | null => {
    let expression = parseNot()
    if (!expression) return null
    while (position < tokenized.tokens.length) {
      const token = tokenized.tokens[position]
      const implicit = beginsExpression(token)
      if (token.type !== 'and' && !implicit) break
      if (token.type === 'and') position += 1
      const right = parseNot()
      if (!right) return null
      expression = { type: 'and', left: expression, right }
    }
    return expression
  }

  const parseOr = (): CodeSearchExpression | null => {
    let expression = parseAnd()
    if (!expression) return null
    while (tokenized.tokens[position]?.type === 'or') {
      position += 1
      const right = parseAnd()
      if (!right) return null
      expression = { type: 'or', left: expression, right }
    }
    return expression
  }

  const expression = parseOr()
  if (!expression || position !== tokenized.tokens.length) {
    return { atoms, expression: null, error: 'Invalid boolean search expression' }
  }
  return { atoms, expression, error: null }
}

export function evaluateCodeSearchExpression(
  expression: CodeSearchExpression | null,
  matches: (atom: CodeSearchAtom) => boolean,
): boolean {
  if (!expression) return true
  if (expression.type === 'atom') return matches(expression.atom)
  if (expression.type === 'not') return !evaluateCodeSearchExpression(expression.expression, matches)
  if (expression.type === 'and') {
    return (
      evaluateCodeSearchExpression(expression.left, matches) &&
      evaluateCodeSearchExpression(expression.right, matches)
    )
  }
  return (
    evaluateCodeSearchExpression(expression.left, matches) ||
    evaluateCodeSearchExpression(expression.right, matches)
  )
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function globPattern(value: string): RegExp {
  const anchored = value.startsWith('/')
  const source = anchored ? value.slice(1) : value
  let pattern = ''
  for (let index = 0; index < source.length; index += 1) {
    const character = source[index]
    if (character === '*' && source[index + 1] === '*') {
      pattern += '.*'
      index += 1
    } else if (character === '*') {
      pattern += '[^/]*'
    } else if (character === '?') {
      pattern += '[^/]'
    } else {
      pattern += escapeRegExp(character)
    }
  }
  return new RegExp(`${anchored ? '^' : ''}${pattern}`, 'i')
}

export function matchesCodeSearchText(atom: CodeSearchAtom, value: string): boolean {
  if (atom.regex) {
    let source = atom.value
    let flags = 'i'
    if (source.startsWith('(?-i)')) {
      source = source.slice(5)
      flags = ''
    }
    try {
      return new RegExp(source, flags).test(value)
    } catch {
      return false
    }
  }
  if (atom.qualifier === 'path' && !atom.quoted && /[*?]/.test(atom.value)) {
    return globPattern(atom.value).test(value)
  }
  if (atom.qualifier === 'path' && !atom.quoted && atom.value.startsWith('/')) {
    return value.toLowerCase().startsWith(atom.value.slice(1).toLowerCase())
  }
  return value.toLowerCase().includes(atom.value.toLowerCase())
}
